import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { OverlayPanel } from "primereact/overlaypanel";
import { useState, useEffect, useRef } from "react";
import { setRows } from "store/slices/serviceDetails";
import { ProcedureRow } from "types/CreateOrder";
import { IPriorAuthFinding } from "types/PriorAuth/PriorAuth";

import { PriorAuthBadge } from "./PriorAuthBadge";

export function ProcedureInfoColumn({
  row,
  rows,
  dispatch,
  priorAuthFinding,
}: {
  row: ProcedureRow;
  rows: ProcedureRow[];
  dispatch: any;
  /**
   * CLE-2766. Optional, and absent on the non-GFE path — a code with no prior-auth finding renders
   * exactly as it did before, which is every code until an operator loads a list for the payer.
   */
  priorAuthFinding?: IPriorAuthFinding;
}) {
  const [showError, setShowError] = useState(!row.description.trim().length);
  const [description, setDescription] = useState("");
  const op = useRef<OverlayPanel>(null);

  useEffect(() => {
    setDescription(row.description);
  }, [row.description]);

  const onDescriptionChange = (e: any) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    if (!newDescription.length) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  };

  const onBlurDescription = () => {
    if (row.description.trim() === description.trim()) return;

    const rowsCopy = [...rows];
    const newRows = rowsCopy.map((aRow) => {
      if (
        aRow.cptCode === row.cptCode &&
        aRow.procedureId === row.procedureId
      ) {
        return {
          ...aRow,
          description: description.trim(),
        };
      }
      return aRow;
    });
    dispatch(setRows(newRows));
  };

  return (
    <div className="flex flex-column">
      <div className="flex flex-row gap-4 align-items-start">
        <div className="flex flex-column flex-2">
          <span className="font-bold">{row.description}</span>
          {/* CLE-2766 — sits under the description, matching the v0 design. Renders null when the
              code has no prior-auth finding, which is the default everywhere. */}
          <PriorAuthBadge finding={priorAuthFinding} />
        </div>
        <Button
          aria-label="Edit description"
          icon="pi pi-pencil"
          className="p-button-text p-button-rounded p-button-sm p-0"
          style={{
            minWidth: "fit-content",
            width: "1.75rem",
            height: "1.75rem",
            color: "var(--brand-gray-600)",
          }}
          onClick={(e) => {
            op?.current?.show(e, e.currentTarget);
          }}
        />
        <OverlayPanel
          ref={op}
          style={{
            width: "500px",
            maxWidth: "100%",
          }}
          dismissable={false}
        >
          <div className="flex flex-column gap-1 w-full align-items-end">
            <InputTextarea
              value={description}
              defaultValue={row.description}
              autoResize
              rows={1}
              style={{ maxWidth: "100%", width: "100%", minWidth: "100%" }}
              onChange={onDescriptionChange}
            />
            {showError && (
              <span className="text-sm" style={{ color: "#e24c4c" }}>
                CPT description can not be empty
              </span>
            )}
            <div className="flex flex-row gap-2">
              <Button
                onClick={(e) => {
                  op?.current?.toggle(e);
                  setDescription(row.description);
                  setShowError(false);
                }}
                className="mt-2"
              >
                <p className="small-text">Close</p>
              </Button>
              <Button
                onClick={(e) => {
                  onBlurDescription();
                  op?.current?.toggle(e);
                }}
                disabled={showError}
                className="mt-2"
              >
                <p className="small-text">Save</p>
              </Button>
            </div>
          </div>
        </OverlayPanel>
      </div>
    </div>
  );
}
